import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { useAuth } from '../../context/AuthContext';
import { buildMediaUrl } from '../../config/api';
import { buildApiUrl } from '../../config/api';
import { fetchJson } from '../../utils/fetchJson';
import { getUnreadMessagesCount } from '../../utils/messages';
import { useSocket } from '../../context/SocketContext';

import DashboardSidebar from './DashboardSidebar';
import ConnectionStatus from '../ConnectionStatus.js';
import ThemeToggleButton from '../ThemeToggleButton';
import ConfirmationModal from '../admin/ConfirmationModal';

const DashboardLayout = () => {

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showWelcomeBackModal, setShowWelcomeBackModal] = useState(false);
  const { user } = useAuth();
  const { notifications } = useSocket();
  const location = useLocation();
  const profilePhotoUrl = buildMediaUrl(user?.profilePhoto);

  useEffect(() => {
    if (location.state?.accountReactivated) {
      setShowWelcomeBackModal(true);
      // Clear navigation state to prevent showing it again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const fetchUnreadMessages = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');

      if (!token) {
        return;
      }

      const { response, data } = await fetchJson(buildApiUrl('/api/dashboard/stats'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        return;
      }

      if (data?.success) {
        const nextUnread = data?.data?.unreadMessages;
        setUnreadCount(getUnreadMessagesCount([{ unread: nextUnread }]));
      }

    } catch (err) {
      console.error('Error fetching unread messages:', err);
    }
  }, []);

  // Fetch unread messages count for mobile badge
  useEffect(() => {
    fetchUnreadMessages();
  }, [fetchUnreadMessages, location.pathname]);

  // Refresh unread when socket notifications arrive
  useEffect(() => {
    if (notifications.length > 0) {
      const timeoutId = setTimeout(() => {
        fetchUnreadMessages(); // stay in sync with server when a notification arrives
      }, 200);
      return () => clearTimeout(timeoutId);
    }
  }, [notifications, fetchUnreadMessages]);

  // Clear unread when global read event fires
  useEffect(() => {
    const handleMessagesRead = () => setUnreadCount(0);
    window.addEventListener('messages_read', handleMessagesRead);
    return () => window.removeEventListener('messages_read', handleMessagesRead);
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Connection Status */}
      <ConnectionStatus />

      {/* Top Navigation Bar - Full Width */}
      <header className="w-full bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
          {/* Left side - Menu button and Logo */}
          <div className="flex items-center">
            {/* Hamburger Menu - Only visible on mobile */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="relative lg:hidden text-gray-500 hover:text-gray-700 focus:outline-none mr-4"
            >
              {unreadCount > 0 && !sidebarOpen && (
                <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-red-500 animate-pulse" aria-hidden="true" />
              )}
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            <div className="flex items-center">
              <div className="flex items-center justify-center w-12 h-12 bg-white rounded-full shadow-lg border border-gray-200">
                <img
                  src="/images/logo.png"
                  alt="MarryCenter Logo"
                  className="w-8 h-8 rounded-full object-cover"
                />
              </div>
              <div className="flex flex-col ml-3">
                <span className="text-2xl font-bold bg-gradient-to-r from-romantic-600 to-primary-600 bg-clip-text text-transparent" style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}>
                  MarryCenter
                </span>
                <span className="text-xs text-gray-500 -mt-1">Find Your Perfect Match</span>
              </div>
            </div>
          </div>

          {/* Right side - Theme toggle + User info */}
          <div className="flex items-center space-x-2">
            <ThemeToggleButton />
            {/* User Profile */}
            <div className="flex items-center space-x-3">
              <div className="text-right hidden md:block">
                <p className="text-sm font-medium text-gray-900 leading-tight">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-gray-500">
                  {user?.membershipType === 'premium' ? '⭐ Premium' : 'Free Member'}
                </p>
              </div>
              <div className="relative w-8 h-8 overflow-visible">
                {unreadCount > 0 && (
                  <span className="absolute -top-2 -right-2 min-h-[18px] min-w-[18px] px-1 rounded-full bg-red-500 text-[10px] leading-[18px] font-semibold text-white ring-2 ring-white flex items-center justify-center z-10">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
                <div className="w-8 h-8 bg-gradient-to-r from-romantic-500 to-primary-500 rounded-full flex items-center justify-center text-white font-semibold text-sm overflow-hidden">
                  {profilePhotoUrl ? (
                    <img
                      src={profilePhotoUrl}
                      alt={`${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'User avatar'}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <span>{(user?.firstName?.charAt(0) || '')}{(user?.lastName?.charAt(0) || '')}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area - Sidebar + Page Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <DashboardSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        {/* Page Content */}
        <div className="flex-1 overflow-y-auto lg:ml-64">
          <main className="w-full px-4 py-6 sm:px-6 lg:px-6">
            <Outlet />
          </main>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showWelcomeBackModal}
        onClose={() => setShowWelcomeBackModal(false)}
        onConfirm={() => setShowWelcomeBackModal(false)}
        title="Welcome Back! 🎉"
        message="Your profile has been successfully reactivated. You are now visible to other users again and can resume your search for a perfect match."
        confirmText="Get Started"
        cancelText={null}
      />
    </div>
  );
};

export default DashboardLayout;